import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Calculator, Shield, Users, Gavel, Clock, GraduationCap, Star, Award, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';

const Services: React.FC = () => {
  const location = useLocation();
  
  // Add canonical tag for services page
  useCanonical('/services');

  // Handle hash navigation when component mounts
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1); // Remove the # symbol
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 80; // Approximate header height
        const elementPosition = element.offsetTop - headerHeight - 20; // Extra 20px padding
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [location.hash]);

  const whyChooseUs = [
    {
      title: 'Expert Team',
      description: 'Certified professionals with years of GST experience',
      icon: Users
    },
    {
      title: 'Technology Driven',
      description: 'Advanced tools and platforms for efficient service delivery',
      icon: Calculator
    },
    {
      title: 'Timely Delivery',
      description: 'Commitment to meeting deadlines and delivering quality work',
      icon: Clock
    },
    {
      title: 'Comprehensive Support',
      description: 'End-to-end support from registration to litigation',
      icon: Shield
    }
  ];

  const stats = [
    { number: '1000+', label: 'Cases Handled', icon: Award },
    { number: '95%', label: 'Success Rate', icon: Star },
    { number: '24/7', label: 'Support Available', icon: Zap },
    { number: '10+', label: 'Years Experience', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* GST Services Overview */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
                         <h2 className="text-3xl md:text-4xl font-bold text-[rgb(19,47,113)] mb-4">
               GST Services
             </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional, straight-to-the-point support covering compliance, reviews, refunds, advisory, and litigation. Clear scope, timely execution, and documentation that stands scrutiny.
            </p>
          </div>

          {/* Service Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {[
              { name: 'Compliance', id: 'compliance-section' },
              { name: 'Review & Audit', id: 'review-audit-section' },
              { name: 'Refund Services', id: 'refund-services-section' },
              { name: 'Advisory & Consultancy', id: 'advisory-section' },
              { name: 'Representation, Appeals & Litigation', id: 'representation-section' }
            ].map((service) => (
              <a
                key={service.id}
                href={`#${service.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById(service.id);
                  if (element) {
                    const headerHeight = 80; // Approximate header height
                    const elementPosition = element.offsetTop - headerHeight - 20; // Extra 20px padding
                    window.scrollTo({
                      top: elementPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-full font-medium border border-gray-300 hover:bg-white hover:text-[#19679E] hover:border-[#19679E] transition-colors duration-200 cursor-pointer"
              >
                {service.name}
              </a>
            ))}
          </div>

          {/* Service Details */}
          <div className="space-y-16">
            {/* 1. Compliance */}
            <div id="compliance-section" className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">1. Compliance</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                GST compliance is continuous and time-bound. We provide end-to-end support for registrations, amendments, and timely filing of monthly/quarterly returns (GSTR-1, 3B) and annual returns with audit (GSTR-9, 9C). We manage 2A/2B-purchase register reconciliations, vendor follow-ups for missing credits, RCM monitoring, and ITC mismatch rectification. The objective is simple: accurate records, on-time filings, and avoidance of penalties or credit blockage.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">GST registration & amendments</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">GSTR-2A/2B vs purchase register reconciliation</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">ITC mismatch rectification guidance</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Monthly/quarterly return filing (GSTR-1, 3B, etc.)</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Vendor follow-up for missing credits</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Annual returns & audit (GSTR-9, 9C)</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">RCM compliance tracking</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Review & Audit */}
            <div id="review-audit-section" className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">2. Review & Audit</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Periodic review reduces downstream risk. We conduct GST health checks, examine filed returns (GSTR-1, 3B, 9/9C), and reconcile books with GST returns. We verify ITC eligibility, support pre-audit compliance checks, assist with internal GST audits, and review agreements for GST exposure. This helps detect issues early and prepares you for departmental scrutiny.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">GST health check of returns and ledgers</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Review of GSTR-1, 3B, 9/9C filings</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Books vs GST return reconciliation</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">ITC eligibility review</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Pre-audit compliance review</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Internal GST audit assistance</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Review of agreements from GST perspective</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Refund Services */}
            <div id="refund-services-section" className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">3. Refund Services</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Refunds affect working capital. We prepare and file refund claims for accumulated ITC under LUT, IGST on exports, inverted duty structure, excess cash ledger balances, and wrongly paid taxes. We also support interest claims for delayed refunds. A structured approach improves the likelihood of timely sanction and reduces cash flow strain.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Refund of accumulated ITC under LUT</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Refund of IGST paid on export</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Refund due to inverted duty structure</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Excess cash ledger balance refunds</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Refund of wrongly paid tax</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Interest on delayed refund claims</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Advisory & Consultancy */}
            <div id="advisory-section" className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">4. Advisory & Consultancy</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Frequent amendments and complex fact patterns require clear guidance. We provide transaction-specific advice, tax-efficient supply chain structuring, written legal opinions, and impact assessments of changes in law. We also advise on cross-border supplies, imports, and exports. The focus is on practical answers that reduce future disputes.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Transaction-specific GST advice</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Impact analysis of GST amendments</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Tax structuring for supply chains</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Cross-border supply & import/export issues</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Written legal opinions</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Representation, Appeals & Litigation */}
            <div id="representation-section" className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">5. Representation, Appeals & Litigation</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                When notices, audits, or disputes arise, precise representation matters. We draft replies, assist during departmental audits and inspections, attend personal hearings, and liaise with authorities. Where escalation is required, we draft and file appeals, prepare legal submissions, and represent before appellate authorities and tribunals. We also handle writ petitions and higher court matters, ensuring continuity of strategy through all stages.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Replying to GST notices & summons</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Liaison with GST authorities</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Writ petitions & higher court matters</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Handling audits & inspections by department</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Adjudication-stage dispute settlement</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Preparation of legal submissions & arguments</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Attending personal hearings</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Drafting & filing appeals; tribunal representation</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Advance ruling assistance</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose TaxYukti?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine expertise, technology, and personalized service to deliver exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
                <div className="bg-primary-lighter w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="text-primary" size={40} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple, transparent, and efficient process to deliver the best results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Consultation</h3>
              <p className="text-gray-600">Initial consultation to understand your requirements</p>
            </div>

            <div className="text-center">
              <div className="bg-primary text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Planning</h3>
              <p className="text-gray-600">Develop a customized strategy for your needs</p>
            </div>

            <div className="text-center">
              <div className="bg-primary text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Execution</h3>
              <p className="text-gray-600">Implement the plan with precision and care</p>
            </div>

            <div className="text-center">
              <div className="bg-primary text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Support</h3>
              <p className="text-gray-600">Ongoing support and monitoring</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-lighter mb-8 max-w-2xl mx-auto">
            Let us help you navigate the complex GST landscape with confidence
          </p>
                     <div className="flex justify-center">
             <Link
               to="/login"
               className="border-2 border-white text-white hover:bg-white hover:text-[#19679E] hover:border-[#19679E] px-8 py-4 rounded-lg font-semibold transition-all duration-200 text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
             >
               Ask Your GST Queries for Free
             </Link>
           </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
